import _isEqual from 'lodash/isEqual';

import { parseClosureFilters } from '@utils/closureFilterUtil';
import { getExternalSourceDisplayName } from '@utils/externalSourceUtil';

export const getCategoryFilterValues = (categories: ICRMCategoryResponse[], selectedIds: number[]) => {
  if (categories && categories.length) {
    const cat = categories.filter((c) => selectedIds.includes(c.id)).map((c) => c.name);

    return cat.join(', ');
  }

  return '-';
};

export const getAssigneeFilterValues = (crmAssignees: ICSUser[], selectedIds: number[]) => {
  if (crmAssignees && crmAssignees.length) {
    const assignees = crmAssignees.filter((c) => selectedIds.includes(c.id)).map((c) => `${c.firstName} ${c.lastName}`);

    return assignees.join(', ');
  }

  return '-';
};

export const getDepartmentFilterValues = (departments: IDepartmentResponse[], selectedIds: number[]) => {
  if (departments && departments.length) {
    const depts = departments.filter((c) => selectedIds.includes(c.id)).map((c) => `${c.label}`);

    return depts.join(', ');
  }

  return '-';
};

export const getCustomerFilterValues = (customers: IContactsListResponse) => {
  if (customers && customers.selectedContacts.length) {
    const custs = customers.selectedContacts.map((c) => `${c.name}`);

    return custs.join(', ');
  }

  return '-';
};

export const getDateFilterValues = (filterLabel: DateFilterType | string) => {
  switch (filterLabel) {
    case DateFilterType.ANYTIME:
      return '';
    case DateFilterType.TODAY:
      return 'Today';
    case DateFilterType.LAST_SEVEN_DAYS:
      return 'Last Seven Days';
    case DateFilterType.LAST_THIRTY_DAYS:
      return 'Last Thirty Days';
    case DateFilterType.OVERDUE:
      return 'Overdue';
    case DateFilterType.NEXT_SEVEN_DAYS:
      return 'Next Seven Days';
    default:
      if (!filterLabel) {
        return '-';
      }

      return filterLabel;
  }
};

export const getChannelFilterValues = (
  channelOptions: IExternalSourceOption[] = [],
  selectedIds: number[] = []
) => {
  if (channelOptions.length && selectedIds.length) {
    return channelOptions
      .filter((channel) => selectedIds.includes(channel.id))
      .map((channel) => channel.name)
      .join(', ');
  }

  return '-';
};

export const getClosureFilterValues = (closureFilters: string[] = [], closureOptions?: ICRMClosureOptions | null) => {
  const { closedReasonIds, closedViaSourceIds, includeNoClosureDetail } = parseClosureFilters(closureFilters);
  const labels: string[] = [];

  const reasonById = new Map((closureOptions?.filterReasons || []).map((reason) => [reason.id, reason.label]));
  const sourceById = new Map((closureOptions?.closedViaSources || []).map((source) => [source.id, source.name]));

  closedReasonIds.forEach((id) => {
    if (reasonById.has(id)) {
      labels.push(reasonById.get(id));
    }
  });

  closedViaSourceIds.forEach((id) => {
    if (sourceById.has(id)) {
      labels.push(`via ${getExternalSourceDisplayName(sourceById.get(id))}`);
    }
  });

  if (includeNoClosureDetail) {
    labels.push('No closure detail');
  }

  return labels.length ? labels.join(', ') : '-';
};

export const getUpdatedFilterValues = (
  selectedfilters: ICustomerSubmissionFilters,
  defaultFilters: ICustomerSubmissionFilters,
  categories: ICRMCategoryResponse[],
  crmAssignees: ICSUser[],
  departments: IDepartmentResponse[],
  customers: IContactsListResponse,
  channelOptions: IExternalSourceOption[] = [],
  closureOptions?: ICRMClosureOptions | null
): IKeyValuePair => {
  const CSLPFiltersLabel = {
    categoryIds: 'Categories',
    departmentIds: 'Departments',
    customerIds: 'Customers',
    assigneeIds: 'Submission Assignee',
    closedAt: 'Closed At',
    createdAt: 'Created At',
    dueDate: 'Due Date',
    emailReadStatus: 'Submissions With Unread Emails',
    escalationStatus: 'Escalated',
    status: 'Status',
    channelIds: 'Channel',
    closureFilters: 'Reason for Closure',
  };

  const filterValues = {};

  for (const key of Object.keys(selectedfilters)) {
    if (!_isEqual(defaultFilters[key], selectedfilters[key])) {
      let value;
      switch (key) {
        case 'categoryIds':
          value = getCategoryFilterValues(categories, selectedfilters[key]);
          break;
        case 'departmentIds':
          value = getDepartmentFilterValues(departments, selectedfilters[key]);
          break;
        case 'customerIds':
          value = getCustomerFilterValues(customers);
          break;
        case 'assigneeIds':
          value = getAssigneeFilterValues(crmAssignees, selectedfilters[key]);
          break;
        case 'closedAt':
        case 'createdAt':
        case 'dueDate':
          value = getDateFilterValues(selectedfilters[key]);
          break;
        case 'emailReadStatus':
        case 'escalationStatus':
          value = 'Yes';
          break;
        case 'status':
          value = selectedfilters[key].join(', ');
          break;
        case 'channelIds':
          value = getChannelFilterValues(channelOptions, selectedfilters[key]);
          break;
        case 'closureFilters':
          value = getClosureFilterValues(selectedfilters[key], closureOptions);
          break;
        default:
          value = JSON.stringify(selectedfilters[key]);
      }

      const label = CSLPFiltersLabel[key];
      filterValues[label] = value;
    }
  }

  return filterValues;
};
